import React, { useState, useEffect } from 'react';
import { supabase } from '../../config/supabase';
import { labelSave, labelDelete } from '../../config/adminApi';
import { ALL_LABEL_KEYS, CAT_LABELS, clearLabelsCache } from '../../translations/tr';

const C = {
  primary: '#5a67d8', purple: '#764ba2',
  green: '#16a34a', red: '#dc2626',
  text: '#1f2937', muted: '#6b7280',
  border: '#e5e7eb', bg: '#f9fafb', white: '#ffffff',
};

export default function LabelsManager({ lang, onClose, onSaved }) {
  const [customLabels, setCustomLabels] = useState({});
  const [editing,      setEditing]      = useState(null); // { key, ar, en }
  const [search,       setSearch]       = useState('');
  const [cat,          setCat]          = useState('all');
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState('');
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    supabase.from('custom_labels').select('*')
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.forEach(l => { map[l.key] = { ar: l.ar_value, en: l.en_value }; });
          setCustomLabels(map);
        }
        setLoading(false);
      });
  }, []);

  const filtered = ALL_LABEL_KEYS.filter(l => {
    if (cat !== 'all' && l.cat !== cat) return false;
    if (search) {
      const s = search.toLowerCase();
      const custom = customLabels[l.key];
      return l.key.includes(s) ||
        (custom?.ar || l.ar).includes(s) ||
        (custom?.en || l.en).toLowerCase().includes(s);
    }
    return true;
  });

  const startEdit = (label) => {
    const custom = customLabels[label.key];
    setEditing({
      key: label.key,
      ar:  custom?.ar ?? label.ar,
      en:  custom?.en ?? label.en,
      defaultAr: label.ar,
      defaultEn: label.en,
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    const category = ALL_LABEL_KEYS.find(l => l.key === editing.key)?.cat || 'general';
    const r = await labelSave(editing.key, editing.ar, editing.en, category);
    setSaving(false);
    if (r?.error) { alert('❌ ' + r.error); return; }

    setCustomLabels(prev => ({ ...prev, [editing.key]: { ar: editing.ar, en: editing.en } }));
    clearLabelsCache();
    setSaved(editing.key);
    setTimeout(() => setSaved(''), 2000);
    setEditing(null);
    onSaved?.();
  };

  const handleReset = async (key) => {
    if (!window.confirm('إعادة المسمى للافتراضي؟')) return;
    const r = await labelDelete(key);
    if (r?.error) { alert('❌ ' + r.error); return; }
    setCustomLabels(prev => { const n = {...prev}; delete n[key]; return n; });
    clearLabelsCache();
    onSaved?.();
  };

  const customCount = Object.keys(customLabels).length;

  return (
    <div style={{ fontFamily: 'Cairo, Arial, sans-serif', direction: 'rtl', padding: '0' }}>

      {/* هيدر */}
      <div style={{ background: `linear-gradient(135deg,${C.primary},${C.purple})`, padding: '20px 24px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 'bold' }}>🏷️ مدير المسميات</h2>
          <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
            {customCount > 0 ? `${customCount} مسمى مخصص` : 'كل المسميات افتراضية'} — {ALL_LABEL_KEYS.length} مصطلح
          </p>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      </div>

      {/* فلاتر */}
      <div style={{ background: C.bg, borderRight: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`, padding: '12px 16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="🔍 ابحث بالمسمى..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ padding: '7px 12px', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', flex: 1, minWidth: '160px', outline: 'none', fontFamily: 'Cairo, sans-serif' }}
        />
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {[['all', '🗂 الكل'], ...Object.entries(CAT_LABELS)].map(([v, l]) => (
            <button key={v} onClick={() => setCat(v)}
              style={{ padding: '6px 12px', border: `1px solid ${cat === v ? C.primary : C.border}`, borderRadius: '8px', fontSize: '11px', fontWeight: cat === v ? 'bold' : '400', background: cat === v ? '#eef2ff' : C.white, color: cat === v ? C.primary : C.text, cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* قائمة المسميات */}
      <div style={{ border: `1px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 16px 16px', overflow: 'hidden', maxHeight: '520px', overflowY: 'auto', background: C.white }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: C.muted, padding: '40px' }}>جاري التحميل...</p>
        ) : filtered.length === 0 ? (
          <p style={{ textAlign: 'center', color: C.muted, padding: '40px' }}>لا توجد نتائج</p>
        ) : filtered.map((label, i) => {
          const custom = customLabels[label.key];
          const isCustom = !!custom;
          const currentAr = custom?.ar ?? label.ar;
          const currentEn = custom?.en ?? label.en;
          const isEditing = editing?.key === label.key;
          const justSaved = saved === label.key;

          return (
            <div key={label.key} style={{ padding: '12px 16px', borderBottom: `1px solid ${C.bg}`, background: isEditing ? '#f0f4ff' : justSaved ? '#f0fdf4' : i % 2 === 0 ? C.white : C.bg, transition: 'background 0.2s' }}>
              {!isEditing ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <code style={{ fontSize: '10px', background: '#eef2ff', color: C.primary, padding: '1px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>{label.key}</code>
                      {isCustom && <span style={{ fontSize: '10px', background: '#fef3c7', color: '#d97706', padding: '1px 6px', borderRadius: '4px', fontWeight: 'bold' }}>مخصص</span>}
                      {justSaved && <span style={{ fontSize: '10px', color: C.green, fontWeight: 'bold' }}>✅ تم الحفظ</span>}
                      <span style={{ fontSize: '10px', color: C.muted }}>{CAT_LABELS[label.cat]}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      <div>
                        <span style={{ fontSize: '10px', color: C.muted }}>عربي: </span>
                        <span style={{ fontSize: '14px', fontWeight: isCustom ? 'bold' : '400', color: isCustom ? C.primary : C.text }}>{currentAr}</span>
                      </div>
                      <div>
                        <span style={{ fontSize: '10px', color: C.muted }}>English: </span>
                        <span style={{ fontSize: '13px', fontWeight: isCustom ? 'bold' : '400', color: isCustom ? C.primary : C.muted, direction: 'ltr' }}>{currentEn}</span>
                      </div>
                    </div>
                    {isCustom && (
                      <div style={{ marginTop: '3px', fontSize: '11px', color: C.muted }}>
                        الافتراضي: {label.ar} / {label.en}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button onClick={() => startEdit(label)}
                      style={{ padding: '6px 14px', background: C.primary, color: C.white, border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Cairo, sans-serif' }}>
                      ✏️ تعديل
                    </button>
                    {isCustom && (
                      <button onClick={() => handleReset(label.key)}
                        style={{ padding: '6px 10px', background: '#fee2e2', color: C.red, border: 'none', borderRadius: '8px', fontSize: '12px', cursor: 'pointer' }}>
                        ↺
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                /* فورم التعديل */
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                    <code style={{ fontSize: '11px', background: '#eef2ff', color: C.primary, padding: '2px 8px', borderRadius: '4px' }}>{label.key}</code>
                    <span style={{ fontSize: '12px', color: C.muted }}>تعديل المسمى</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ fontSize: '11px', color: C.muted, display: 'block', marginBottom: '4px', fontWeight: '600' }}>المسمى بالعربي</label>
                      <input
                        value={editing.ar}
                        onChange={e => setEditing(prev => ({ ...prev, ar: e.target.value }))}
                        placeholder={label.ar}
                        autoFocus
                        style={{ width: '100%', padding: '8px 12px', border: `2px solid ${C.primary}`, borderRadius: '8px', fontSize: '14px', fontFamily: 'Cairo, sans-serif', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', color: C.muted, display: 'block', marginBottom: '4px', fontWeight: '600' }}>المسمى بالإنجليزي</label>
                      <input
                        value={editing.en}
                        onChange={e => setEditing(prev => ({ ...prev, en: e.target.value }))}
                        placeholder={label.en}
                        dir="ltr"
                        style={{ width: '100%', padding: '8px 12px', border: `2px solid ${C.primary}`, borderRadius: '8px', fontSize: '13px', fontFamily: 'Arial, sans-serif', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button onClick={handleSave} disabled={saving}
                      style={{ padding: '8px 20px', background: `linear-gradient(135deg,${C.primary},${C.purple})`, color: C.white, border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', opacity: saving ? 0.7 : 1 }}>
                      {saving ? '⏳ جاري الحفظ...' : '💾 حفظ'}
                    </button>
                    <button onClick={() => setEditing(null)}
                      style={{ padding: '8px 16px', background: C.bg, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', cursor: 'pointer', fontFamily: 'Cairo, sans-serif', color: C.text }}>
                      إلغاء
                    </button>
                    <span style={{ fontSize: '11px', color: C.muted }}>
                      افتراضي: <strong>{editing.defaultAr}</strong> / <strong>{editing.defaultEn}</strong>
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ملاحظة */}
      <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', fontSize: '12px', color: '#92400e' }}>
        ⚠️ التغييرات تظهر فوراً بعد الحفظ — لو مش شايف التغيير refresh الصفحة مرة واحدة.
      </div>
    </div>
  );
}
